/// <amd-module name="TFS/Admin/Views/SurveyPanel/SurveyPanel" />

import * as React from "react";

import { AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { Components, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { ContributedFeature, ContributedFeatureEnabledValue } from "TFS/FeatureManagement/Contracts/FeatureManagement";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { Checkbox } from "VSSUI/Checkbox";
import { ContentSize } from "VSSUI/Callout";
import { FormItem } from "VSSUI/FormItem";
import { IButtonProps } from "VSSUI/Button";
import { ITfsFeatureService } from "TFS/FeatureManagement/Types";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { ObservableValue } from "VSS/Core/Observable";
import { Panel } from "VSSUI/Panel";
import { TextField } from "VSSUI/TextField";

import { ISurveyPanelProps } from "TFS/Admin/Views/SurveyPanel/admin-survey-panel.Props";

import AdminResources = require("TFS/Admin/Views/SurveyPanel/Resources");
import { Icon, IconSize } from "VSSUI/Icon";

export interface SurveyPanelState {
    isProcessing: boolean;
}

const SurveyPanelAllowContact = "AllowContact";
const MaximumReasonLength = 1024;

export class SurveyPanelComponent extends VssComponent<ISurveyPanelProps, SurveyPanelState> {
    private _feedbackText = new ObservableValue("");

    private _allowContact = new ObservableValue<boolean>(false);
    private showFeedbackLimitWarning = new ObservableValue<boolean>(false);
    private featureService: ITfsFeatureService;
    private _feature: ContributedFeature | undefined;

    constructor(props: ISurveyPanelProps, context?: any) {
        super(props, context);
        this.featureService = props.pageContext.getService<ITfsFeatureService>("ITfsFeatureService");
        this.state = {
            isProcessing: false
        };
    }

    public componentDidMount() {
        const actionTelemetryLogger = this._startActionCapture("Survey Panel Did Mount", {});
        this.featureService.getFeatureDefinitions(undefined, true).then(
            features => {
                if (features.devModeFeatures) {
                    this._feature = features.devModeFeatures.find(s => s.id == this.props.feature);
                } else if (features.features) {
                    this._feature = features.features.find(s => s.id == this.props.feature);
                }
            },
            reject => {
                actionTelemetryLogger.captureError(reject.message || String(reject));
            }
        );
    }

    public render(): JSX.Element {
        const footerButtons: IButtonProps[] = [
            {
                onClick: this.props.onCancel,
                text: AdminResources.CancelButton
            },
            {
                onClick: () => {
                    this.setState({ isProcessing: true });
                    this._submitFeedback();
                },
                primary: true,

                text: AdminResources.SubmitButton
            }
        ];
        return (
            <Panel
                onDismiss={this.props.onCancel}
                titleProps={{ text: AdminResources.SurveyTitle }}
                description={AdminResources.SurveyDescription}
                size={ContentSize.Large}
                footerButtonProps={footerButtons}
            >
                {this.state.isProcessing ? (
                    <div className="flex-column flex-grow feedback-text-field-div">
                        <Spinner size={SpinnerSize.large} />
                    </div>
                ) : (
                    <div className="flex-column flex-grow feedback-text-field-div">
                        <FormItem label={AdminResources.SurveySubtitle}>
                            <TextField
                                inputClassName="feedback-text-field"
                                multiline={true}
                                onChange={this._onFeedbackChanged}
                                value={this._feedbackText}
                                autoAdjustHeight={true}
                                maxLength={MaximumReasonLength}
                            />
                            <Observer showFeedbackLimitWarning={this.showFeedbackLimitWarning}>
                                {(props: { showFeedbackLimitWarning: boolean }) =>
                                    props.showFeedbackLimitWarning ? (
                                        <div className="margin-left-8 margin-top-8 feedback-limit-warning">
                                            <Icon className="margin-right-4" iconName={"Info"} size={IconSize.small} />
                                            <span>{AdminResources.FeedbackTextLimitError}</span>
                                        </div>
                                    ) : null
                                }
                            </Observer>
                            <Checkbox
                                className="margin-top-8"
                                label={AdminResources.AllowMicrosoftToContactForDetails}
                                onChange={this._onAllowContactChanged}
                                checked={this._allowContact}
                            />
                            <Link
                                className="margin-top-8"
                                target="_blank"
                                rel="noopener noreferrer"
                                href={AdminResources.TurnOffFeatureDialogPrivacyLink}
                            >
                                {AdminResources.TurnOffFeatureDialogPrivacyText}
                            </Link>
                        </FormItem>
                    </div>
                )}
            </Panel>
        );
    }

    private _onAllowContactChanged = (
        event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>,
        checked: boolean
    ): void => {
        this._allowContact.value = checked;
    };

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.props.telemetryLogger.startActionCapture(Feature.SettingsSearch, Feature.SettingsSearch, action, properties);
    };

    private _onFeedbackChanged = (event: React.ChangeEvent<HTMLInputElement>, text: string): void => {
        this.showFeedbackLimitWarning.value = text.length >= MaximumReasonLength;
        this._feedbackText.value = text;
    };

    private _submitFeedback(): void {
        const actionTelemetryLogger = this._startActionCapture("Submit feedback", {});
        if (this._feature) {
            this.featureService
                .setFeatureState(
                    this._feature,
                    true,
                    undefined,
                    undefined,
                    ContributedFeatureEnabledValue.Enabled,
                    this._feedbackText.value,
                    this._allowContact.value ? SurveyPanelAllowContact : undefined
                )
                .then(
                    result => {
                        this._feedbackCleanUp();
                    },
                    reject => {
                        this._feedbackCleanUp();
                        actionTelemetryLogger.captureError("Submit feedback error");
                    }
                );
        } else {
            actionTelemetryLogger.captureError("Feature not found");
            this._feedbackCleanUp();
        }
    }

    private _feedbackCleanUp() {
        this.setState({ isProcessing: false });
        this.props.onCompleted();
        this.props.onCancel();
    }
}

VssComponent.register("ms.vss-admin-web.admin-survey-panel", SurveyPanelComponent);
